interface RequestConfig extends RequestInit {
  token?: string;
}

class ApiError extends Error {
  status: number;
  data?: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export async function apiClient<TResponse = unknown, TRequest = unknown>(
  url: string,
  config: RequestConfig & { json?: TRequest } = {},
): Promise<TResponse> {
  // For superadmin endpoints, use elevated token if available
  const isElevatedEndpoint =
    url.includes('/superadmin') || url.includes('/api/firms') || url.includes('/api/users');
  const elevatedToken = sessionStorage.getItem('elevatedToken');

  const headers: Record<string, string> = {
    ...(config.headers as Record<string, string>),
  };

  // Only add Authorization header for elevated endpoints with explicit token
  if (config.token) {
    headers.Authorization = `Bearer ${config.token}`;
  } else if (isElevatedEndpoint && elevatedToken) {
    headers.Authorization = `Bearer ${elevatedToken}`;
  }
  // For regular endpoints, rely on HTTP-only cookies (no Authorization header needed)

  let body = config.body;

  // Handle json property with type safety
  if (config.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(config.json);
  } else if (
    body &&
    typeof body === 'object' &&
    !(body instanceof FormData) &&
    !(body instanceof URLSearchParams) &&
    !(body instanceof Blob)
  ) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(body);
  }

  const response = await fetch(url, {
    ...config,
    headers,
    body,
    credentials: 'include', // Important: Include cookies in requests
  });

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    let errorData: unknown;

    try {
      errorData = await response.json();
      if (typeof errorData === 'object' && errorData !== null) {
        if ('error' in errorData && typeof errorData.error === 'string') {
          errorMessage = errorData.error;
        } else if ('message' in errorData && typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
      }
    } catch {
      // If response is not JSON, use default error message
    }

    // Clear invalid elevated tokens on 401 errors (HTTP-only cookies are handled by server)
    if (response.status === 401) {
      localStorage.removeItem('elevatedToken');
      localStorage.removeItem('user');
      localStorage.removeItem('firm');
      localStorage.removeItem('availableServices');
    }

    throw new ApiError(errorMessage, response.status, errorData);
  }

  // Handle empty responses
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as TResponse;
  }

  return response.json();
}

// Convenience methods
export const api = {
  get: <TResponse = unknown>(url: string, config?: RequestConfig) =>
    apiClient<TResponse>(url, { ...config, method: 'GET' }),

  post: <TResponse = unknown, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: RequestConfig,
  ) => apiClient<TResponse, TRequest>(url, { ...config, method: 'POST', json: data }),

  put: <TResponse = unknown, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: RequestConfig,
  ) => apiClient<TResponse, TRequest>(url, { ...config, method: 'PUT', json: data }),

  patch: <TResponse = unknown, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: RequestConfig,
  ) => apiClient<TResponse, TRequest>(url, { ...config, method: 'PATCH', json: data }),

  delete: <TResponse = unknown>(url: string, config?: RequestConfig) =>
    apiClient<TResponse>(url, { ...config, method: 'DELETE' }),
};
